import React, { useState, useEffect, useRef } from 'react'
import { Alert, Box, Typography, Select, MenuItem, FormControl, Divider, TextField, IconButton, InputAdornment, Drawer } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import TuneIcon from '@mui/icons-material/Tune'
import CloseIcon from '@mui/icons-material/Close'
import ClearIcon from '@mui/icons-material/Clear'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import useAccount from '../../hooks/useAccount'
import useLoading from '../../hooks/useLoading'
import useSnackbar from '../../hooks/useSnackbar'

import Tree from '../tree/Tree'
import AddSelectionButton from '../tree/AddSelectionButton'
import AxisEditor from './AxisEditor'
import FilterEditorModal from './FilterEditorModal'
import SpecSettingsDrawer from './SpecSettingsDrawer'
import SpecEditorSaveDialog from './SpecEditorSaveDialog'
import SpecRunPreviewDialog from './SpecRunPreviewDialog'
import { CarbonSpec } from '../../types/carbon/models/CarbonSpec'
import SideBarPage from '../system/SidebarPage'
import PageHeader from '../system/PageHeader'
import ActionButton from '../system/ActionButton'
import useVariableTree from '../../hooks/useVariableTree'
import useSpecEditor, { SpecEditorDriver } from '../../hooks/useSpecEditor'
import useConfig from '../../hooks/useConfig'
import { andFilters, createFilterFromSelectedVariables } from '../../utils/filter'
import axios from 'axios'
import { API_BASE_URL } from '../../constants/system'
import { extractErrorMessage } from '../../utils/apitools'
import { GenNode } from '../../types/carbon/models/GenNode'
import { getNodeTitle } from '../../utils/nodeTypes'
import { VoiceUITool } from '../../voice/types'
import { useVoiceRegistration } from '../../voice/useVoiceRegistration'
import { buildEditSpecVoiceTools, SpecAxis } from '../../voice/editSpecVoiceTools'
import PageAgentPane from '../../agent/components/PageAgentPane'
import PageAgentButton from '../../agent/components/PageAgentButton'
import usePageAgentHost from '../../hooks/usePageAgentHost'
import { INSIGHTS_DRAWER_WIDTH } from '../chat/ChatDrawer'

// The spec being restored on mount. On the EditSpec route this comes out of the
// URL, which is why it used to be read here; as props it can come from anywhere,
// which is the point of the extraction (#968).
export interface SpecEditorInitialSyntax {
  top?: string
  side?: string
  filter?: string
}

/**
 * Table-placement policy for the Save action (#968 Part B). SpecEditor stays
 * dashboard-agnostic — it just writes wherever this says. The caller (the
 * Edit Data overlay) works out targetFolder/allowOverwrite from the
 * fork-vs-overwrite rules via resolveTableSaveRules.
 */
export interface SpecEditorSaveConfig {
  /** Folder segment a fork or brand-new table lands in, e.g. "q3-tracker-a1b2c3d4". */
  targetFolder: string
  /** Folder segment overwrite writes back into — the table's own existing folder. */
  originalFolder: string
  /** Whether overwrite-in-place can be offered at all. */
  allowOverwrite: boolean
}

export interface SpecEditorProps {
  customer: string
  job: string
  /** Full table path including the .cbt suffix, or '' for a new table. */
  table: string
  /** Display name shown in the header. Falls back to a bare 'Edit Spec'. */
  tableTitle?: string
  initialDriver?: SpecEditorDriver
  initialLabels?: Record<string, string[]>
  /** Applied once on mount, after the editor has loaded. */
  initialSyntax?: SpecEditorInitialSyntax
  /** Called after the draft is cleared. The caller decides where to go. */
  onCancel: () => void
  /**
   * Where Run Spec and Save go afterwards. Defaults to the Tables page, which
   * is what the /editspec route wants; the Edit Data overlay passes its own,
   * because navigating away would discard the dashboard being edited.
   */
  onNavigateToTables?: (params: Record<string, string>) => void
  /**
   * When set, shows a Save action that writes into this table-placement
   * policy. Undefined = no Save action — today's /editspec behaviour, where
   * Run Spec navigates to the Tables page, which owns Save. The Edit Data
   * overlay is the only caller that sets this, because inside a modal there
   * is no Tables page to navigate to.
   */
  saveConfig?: SpecEditorSaveConfig
  /**
   * When true, Run Spec shows an inline popup table preview of the spec
   * result instead of stashing the spec and navigating to the Tables page
   * (#968 Obs 5). The Edit Data overlay sets this — "running on Tables
   * page" is meaningless inside a modal. The preview is ephemeral: Close
   * Preview returns to Edit Spec with nothing written anywhere.
   */
  inlineRunPreview?: boolean
  /**
   * Register the Edit Spec page tools (voice/editSpecVoiceTools) for the
   * voice assistant, the Help pane and tours. Only the /editspec route sets
   * it: inside the dashboard's Edit Data overlay a registration would replace
   * the dashboard's own tools and leave it with none once the overlay closed.
   * It also gives the page its helper (the agent with these tools), for the
   * same reason.
   */
  registerPageTools?: boolean
}

/** What the Edit Spec page tools read and do, set from each render. */
interface EditSpecLive {
  table: { path: string; title: string }
  axes: Record<SpecAxis, { nodes: GenNode[]; set: (nodes: GenNode[]) => void; syntax: string; error: string | null }>
  variableNodes: GenNode[]
  loadCodes: (node: GenNode) => Promise<GenNode[]>
  filter: string
  weight: string
  caseFilter: string
  setFilter: (expression: string) => void
  setWeight: (expression: string) => void
  validate: (expression: string) => Promise<string | null>
  run: () => void
  cancel: () => void
  openSettings: () => void
}

/** The Edit Spec page as the voice assistant and the Help pane read it. */
function editSpecContext(l: EditSpecLive): string {
  const items = (nodes: GenNode[]) => nodes.map((n) => getNodeTitle(n) || n.value1).filter(Boolean).join(', ') || 'none'
  return [
    `Edit Spec for ${l.table.title ? `the table "${l.table.title}"` : 'a new table'}.`,
    `Rows (side axis): ${items(l.axes.side.nodes)}.`,
    `Columns (top axis): ${items(l.axes.top.nodes)}.`,
    `Filter: ${l.filter || 'none'}. Weight: ${l.weight || 'none'}.`,
    'Nothing here is saved: Run Spec opens the result on Tables as a preview, where the person saves it with Save Table or drops it with Discard.',
  ].join(' ')
}

/**
 * The spec editor, with no knowledge of the route it is on.
 *
 * Everything it used to read from `useRouter`/`useTablePath` now arrives as
 * props, so it can be rendered inside the dashboard chart widget's Edit Data
 * overlay as well as on the /editspec page (#968 Part B). `pages/EditSpec.tsx`
 * is the route wrapper that maps URL params onto these props.
 */
export const SpecEditor: React.FC<SpecEditorProps> = ({
  customer,
  job,
  table,
  tableTitle,
  initialDriver = 'carbon',
  initialLabels,
  initialSyntax,
  onCancel,
  onNavigateToTables,
  saveConfig,
  inlineRunPreview = false,
  registerPageTools = false,
}) => {
  const account = useAccount()
  const loading = useLoading()
  const snackbar = useSnackbar()

  // Driver selection state
  const [driver, setDriver] = useState<SpecEditorDriver>(initialDriver)

  // Labels state
  const [selectedLabels, setSelectedLabels] = useState<Record<string, string[]>>(
    () => initialLabels || {}
  )

  // Settings modal state
  const [settingsOpen, setSettingsOpen] = useState(false)
  const settingsSnapshotRef = useRef<string>('')

  // Context config for the current table
  const config = useConfig(customer, job)

  // Use the variable tree with driver support
  const variableTree = useVariableTree({
    customer,
    job,
    driver,
  })

  const specEditor = useSpecEditor({
    customer,
    job,
    table,
    selectedVariables: variableTree.selectedVariables,
    resetChecked: variableTree.resetChecked,
    driver,
    selectedLabels,
    onNavigateToTables,
  })

  // Manage global loading spinner while data loads
  useEffect(() => {
    const isPageLoading = variableTree.isLoading || specEditor.isLoading
    loading.setLoading(isPageLoading)
  }, [variableTree.isLoading, specEditor.isLoading])

  // Track whether we've restored the incoming syntax
  const hasRestoredInitialSyntax = useRef(false)

  // Restore syntax handed in by the caller (the AutoCoder round-trip through
  // the Tables page puts it in the URL; the wrapper reads it back out).
  useEffect(() => {
    if (hasRestoredInitialSyntax.current) return
    if (!initialSyntax) return

    const { top = '', side = '', filter = '' } = initialSyntax

    hasRestoredInitialSyntax.current = true

    // Apply the syntax asynchronously after the component has mounted
    const restoreSpec = async () => {
      if (top) {
        try {
          await specEditor.handleTopSyntaxApply(top)
        } catch (err) {
          console.warn('Failed to restore top axis syntax:', err)
        }
      }
      if (side) {
        try {
          await specEditor.handleSideSyntaxApply(side)
        } catch (err) {
          console.warn('Failed to restore side axis syntax:', err)
        }
      }
      if (filter) {
        specEditor.setFilter(filter)
      }
    }

    restoreSpec()
  }, [initialSyntax?.top, initialSyntax?.side, initialSyntax?.filter])

  const handleApplyTreeSelection = () => {
    const currentSelection = createFilterFromSelectedVariables(variableTree.selectedVariables, variableTree.nodeMap)
    if (currentSelection) {
      const newFilter = andFilters(specEditor.filter, currentSelection)
      specEditor.setFilter(newFilter)
      variableTree.resetChecked()
    }
  }

  const handleApplyTreeSelectionToWeight = () => {
    const selected = variableTree.selectedVariables
    if (selected.length === 0) return
    const varName = selected[0].value1 || ''
    specEditor.handleFiltersChange({ weight: `${varName}()` })
    variableTree.resetChecked()
  }


  const isManager = account.user?.role_permissions?.isManager || false

  const sourceSelector = isManager ? (
    <FormControl size="small" fullWidth sx={{ mb: 1 }}>
      <Select
        value={driver}
        onChange={(e) => setDriver(e.target.value as SpecEditorDriver)}
        inputProps={{ 'aria-label': 'Data source' }}
      >
        <MenuItem value="carbon">Survey Data</MenuItem>
        <MenuItem value="autocoder">AutoCoder Data</MenuItem>
      </Select>
    </FormControl>
  ) : null

  const sidebar = !variableTree.isLoading && job && variableTree.nodes && variableTree.nodes.length > 0 && (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Tree
        searchEnabled={true}
        searchPlaceholder="Search variables..."
        showTooltips
        nodes={variableTree.nodes}
        expanded={variableTree.expanded}
        getCheckboxState={variableTree.getCheckboxState}
        onNodeExpandToggle={variableTree.onNodeExpandToggle}
        onNodeCheckedToggle={variableTree.onNodeCheckedToggle}
      />
    </Box>
  )

  const handleCancel = () => {
    specEditor.onClearDraft()
    onCancel()
  }

  // Inline Run Spec preview (#968 Obs 5). Holding the built spec doubles as
  // the dialog's open flag; it is dropped again on Close Preview, so nothing
  // outlives the popup.
  const [runPreviewSpec, setRunPreviewSpec] = useState<CarbonSpec | null>(null)

  const handleRunSpec = () => {
    if (!inlineRunPreview) {
      specEditor.onRunSpec()
      return
    }
    setRunPreviewSpec(specEditor.buildCarbonSpec())
  }

  // Save (#968 Part B), matching the Tables page's own handleSaveTable: the
  // save dialog needs a previewSpec built from the *current* editor state,
  // not whatever a prior Run Spec produced. If a preview already exists, use
  // it; otherwise build one, load it, and let the effect below open the
  // dialog once loading completes.
  const pendingSaveRef = useRef(false)
  useEffect(() => {
    if (pendingSaveRef.current && specEditor.previewSpec) {
      pendingSaveRef.current = false
      specEditor.onSaveSpec()
    }
  }, [specEditor.previewSpec])

  const handleSaveClick = () => {
    if (specEditor.previewSpec) {
      specEditor.onSaveSpec()
      return
    }
    const spec = specEditor.buildCarbonSpec()
    try {
      window.sessionStorage.setItem(specEditor.sessionStorageKeys.spec, JSON.stringify(spec))
    } catch {}
    pendingSaveRef.current = true
    specEditor.onLoadPreview?.()
  }

  const handleSaveConfirm = (result: { name: string; subtitle: string; overwrite: boolean }) => {
    if (!saveConfig) return
    specEditor.onConfirmSave({
      name: result.name,
      subtitle: result.subtitle,
      overwrite: result.overwrite,
      destination: 'user',
      userFolder: result.overwrite ? saveConfig.originalFolder : saveConfig.targetFolder,
    })
  }

  const handleShowSettings = () => {
    settingsSnapshotRef.current = JSON.stringify({
      displayProperties: specEditor.displayProperties,
      specProperties: specEditor.specProperties,
    })
    if (table) {
      config.handleTableSettings(table)
    }
    setSettingsOpen(true)
  }

  // The Edit Spec page tools (Help pane live tours PR 2). Built once; every
  // tool reads the editor through liveRef, set from each render below, as
  // the Tables page does.
  const liveRef = useRef<EditSpecLive | null>(null)
  const live = () => {
    if (!liveRef.current) throw new Error('Edit Spec is not ready')
    return liveRef.current
  }
  const pageToolsRef = useRef<VoiceUITool[] | null>(null)
  if (!pageToolsRef.current) {
    pageToolsRef.current = buildEditSpecVoiceTools({
      table: () => live().table,
      axis: (axis) => ({
        nodes: () => live().axes[axis].nodes,
        set: (nodes) => live().axes[axis].set(nodes),
        syntax: () => live().axes[axis].syntax,
        error: () => live().axes[axis].error,
      }),
      variables: { nodes: () => live().variableNodes, loadCodes: (node) => live().loadCodes(node) },
      filter: { get: () => live().filter, set: (e) => live().setFilter(e), validate: (e) => live().validate(e) },
      weight: { get: () => live().weight, set: (e) => live().setWeight(e) },
      caseFilter: () => live().caseFilter,
      run: () => live().run(),
      cancel: () => live().cancel(),
      openSettings: () => live().openSettings(),
    })
  }

  // A variable's codes for the page tools, fetched directly as the Tables
  // page's tools do: the tree hook's own loader reads its loading state from
  // the render it closed over, which inside one tool call can be a render
  // behind and answer no codes.
  const loadVariableCodes = async (node: GenNode): Promise<GenNode[]> => {
    const driverParam = driver === 'autocoder' ? '&storageDriver=autocoder' : ''
    const res = await axios.get<GenNode[]>(`${API_BASE_URL}/job/${customer}/${job}/vartree/codes?variable_id=${encodeURIComponent(node.value1 || '')}${driverParam}`)
    return res.data?.[0]?.children || []
  }

  const validateExpression = async (expression: string): Promise<string | null> => {
    try {
      await axios.post(`${API_BASE_URL}/tables/${customer}/${job}/validate/expression`, { syntax: expression })
      return null
    } catch (err) {
      return extractErrorMessage(err)
    }
  }

  liveRef.current = {
    table: { path: table, title: tableTitle || '' },
    axes: {
      side: { nodes: specEditor.sideNodes || [], set: specEditor.handleSideAxisChange, syntax: specEditor.sideSyntax || '', error: specEditor.sideAxisError ?? null },
      top: { nodes: specEditor.topNodes || [], set: specEditor.handleTopAxisChange, syntax: specEditor.topSyntax || '', error: specEditor.topAxisError ?? null },
    },
    variableNodes: variableTree.nodes || [],
    loadCodes: loadVariableCodes,
    filter: specEditor.filter || '',
    weight: specEditor.weight || '',
    caseFilter: specEditor.caseFilter || '',
    setFilter: (expression) => specEditor.handleFiltersChange({ filter: expression }),
    setWeight: (expression) => specEditor.handleFiltersChange({ weight: expression }),
    validate: validateExpression,
    run: handleRunSpec,
    cancel: handleCancel,
    openSettings: handleShowSettings,
  }

  // The Edit Spec helper (Douglas plan T13): the agent, with this page's tools.
  // Only on the /editspec route; the Edit Data overlay has none.
  const [helperOpen, setHelperOpen] = useState(false)
  // Mounted once opened, so closing keeps the chat for a reopen (resumeKey).
  const [helperMounted, setHelperMounted] = useState(false)
  if (helperOpen && !helperMounted) setHelperMounted(true)
  const pageAgentHost = usePageAgentHost(customer)

  useVoiceRegistration({
    pageKey: `editspec:${customer}:${job}:${table}`,
    uiTools: pageToolsRef.current,
    pageContext: () => (liveRef.current ? editSpecContext(liveRef.current) : ''),
    enabled: registerPageTools,
  })

  return (
    <SideBarPage
      title="Variable Tree"
      headerContent={sourceSelector}
      sidebar={ sidebar }
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Page header — title + action buttons */}
        <PageHeader
          title={tableTitle ? `Edit Spec — ${tableTitle}` : 'Edit Spec'}
          subtitle=""
          actions={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {registerPageTools && (
                <PageAgentButton
                  pageKey="editspec"
                  customer={customer}
                  open={helperOpen}
                  onClick={() => setHelperOpen(open => !open)}
                  label="Edit Spec helper"
                />
              )}
              <ActionButton icon={<PlayArrowIcon />} label="Run Spec" onClick={handleRunSpec} />
              <ActionButton icon={<TuneIcon />} label="Settings" onClick={handleShowSettings} />
              {saveConfig && (
                <ActionButton icon={<SaveIcon />} label="Save Table" onClick={handleSaveClick} />
              )}
              <ActionButton icon={<CloseIcon />} label="Cancel" onClick={handleCancel} />
            </Box>
          }
        />

        {/* Filter / Weight / Case Filter bar */}
        <Box sx={{
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
          px: 3,
          pt: 2.5,
          pb: 1.5,
          backgroundColor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
          <Typography variant="subtitle2" sx={{
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontSize: '0.75rem',
            mr: 0.5,
          }}>
            Filter and Weighting
          </Typography>

          <Divider orientation="vertical" flexItem sx={{ my: '4px' }} />

          {/* Filter */}
          <AddSelectionButton
            onClick={handleApplyTreeSelection}
            active={variableTree.selectedVariables.length > 0}
            label="Add filter"
          />
          <TextField
            value={specEditor.filter || ''}
            label="Filter"
            placeholder="Filter expression"
            size="small"
            sx={{ flex: 1, maxWidth: 320 }}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end" sx={{ gap: 0 }}>
                  {specEditor.filter && (
                    <IconButton size="small" onClick={() => specEditor.handleFiltersChange({ filter: '' })} aria-label="Clear filter" sx={{ p: 0.25 }}>
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={() => specEditor.handleOpenFilterModal()} aria-label="Edit filter" sx={{ p: 0.25 }}>
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Divider orientation="vertical" flexItem sx={{ my: '4px' }} />

          {/* Weight */}
          <AddSelectionButton
            onClick={handleApplyTreeSelectionToWeight}
            active={variableTree.selectedVariables.length > 0}
            label="Add weight"
          />
          <TextField
            value={specEditor.weight || ''}
            onChange={(e) => specEditor.handleFiltersChange({ weight: e.target.value })}
            label="Weight"
            placeholder="WeightCombo()"
            size="small"
            sx={{ width: 180 }}
            InputProps={{
              endAdornment: specEditor.weight ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => specEditor.handleFiltersChange({ weight: '' })} aria-label="Clear weight" sx={{ p: 0.25 }}>
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />

          <Divider orientation="vertical" flexItem sx={{ my: '4px' }} />
          <TextField
            value={specEditor.caseFilter || ''}
            onChange={(e) => specEditor.handleFiltersChange({ caseFilter: e.target.value })}
            label="Case Filter"
            placeholder="BaseCategoryFilter"
            size="small"
            sx={{ width: 180 }}
            InputProps={{
              endAdornment: specEditor.caseFilter ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => specEditor.handleFiltersChange({ caseFilter: '' })} aria-label="Clear case filter" sx={{ p: 0.25 }}>
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>

        {specEditor.errorMessage && (
          <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
            {specEditor.errorMessage}
          </Alert>
        )}

        {/* Axes Editors — edge to edge with divider between */}
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <AxisEditor
              title="Side Axis Editor"
              nodes={specEditor.sideNodes || []}
              syntax={specEditor.sideSyntax}
              onChange={specEditor.handleSideAxisChange}
              onSyntaxApply={specEditor.handleSideSyntaxApply}
              onAddSelected={specEditor.handleAddSelectedToSideAxis}
              onOpenFilterModal={specEditor.handleOpenFilterModal}
              hasSelectedVariables={variableTree.selectedVariables.length > 0}
              error={specEditor.sideAxisError}
            />
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <AxisEditor
              title="Top Axis Editor"
              nodes={specEditor.topNodes || []}
              syntax={specEditor.topSyntax}
              onChange={specEditor.handleTopAxisChange}
              onSyntaxApply={specEditor.handleTopSyntaxApply}
              onAddSelected={specEditor.handleAddSelectedToTopAxis}
              onOpenFilterModal={specEditor.handleOpenFilterModal}
              hasSelectedVariables={variableTree.selectedVariables.length > 0}
              error={specEditor.topAxisError}
            />
          </Box>
        </Box>
      </Box>

      {/* Settings Drawer */}
      <SpecSettingsDrawer
        open={settingsOpen}
        onClose={() => {
          const current = JSON.stringify({
            displayProperties: specEditor.displayProperties,
            specProperties: specEditor.specProperties,
          })
          if (current !== settingsSnapshotRef.current) {
            snackbar.success('Settings updated')
          }
          setSettingsOpen(false)
        }}
        displayProperties={specEditor.displayProperties}
        specProperties={specEditor.specProperties}
        onDisplayPropertiesChange={specEditor.setDisplayProperties}
        onSpecPropertiesChange={specEditor.setSpecProperties}
        mergedConfig={config.mergedConfig}
        loadingConfig={config.loadingConfig}
        savingConfig={config.savingConfig}
        onSaveConfig={config.handleSaveConfig}
      />

      {/* Filter Editor Modal */}
      <FilterEditorModal
        open={specEditor.filterModalOpen}
        onClose={specEditor.handleCloseFilterModal}
        onSave={specEditor.handleSaveFilter}
        currentFilter={
          specEditor.filterTargetNode?.type === 'Filter'
            ? specEditor.filterTargetNode.value1 || ''
            : specEditor.filterTargetNode
            ? ''
            : specEditor.filter
        }
      />

      {/* Inline Run Spec preview (#968 Obs 5) */}
      {inlineRunPreview && (
        <SpecRunPreviewDialog
          open={Boolean(runPreviewSpec)}
          customer={customer}
          job={job}
          spec={runPreviewSpec}
          onClose={() => setRunPreviewSpec(null)}
        />
      )}

      {/* Save Dialog (#968 Part B) */}
      {saveConfig && (
        <SpecEditorSaveDialog
          open={specEditor.saveDialogOpen}
          originalName={tableTitle || ''}
          allowOverwrite={saveConfig.allowOverwrite}
          saving={specEditor.savingPreview}
          onSave={handleSaveConfirm}
          onCancel={specEditor.onCancelSave}
        />
      )}

      {/* The Edit Spec helper: persistent, so the editor stays usable while it works. */}
      {registerPageTools && (
        <Drawer
          anchor="right"
          variant="persistent"
          open={helperOpen}
          onClose={() => setHelperOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: INSIGHTS_DRAWER_WIDTH.width,
              minWidth: INSIGHTS_DRAWER_WIDTH.minWidth,
              maxWidth: INSIGHTS_DRAWER_WIDTH.maxWidth,
              height: '100vh',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 0,
            },
          }}
        >
          {helperMounted && (
            <PageAgentPane
              pageKey="editspec"
              customer={customer}
              job={job}
              open={helperOpen}
              // The path only: the URL's query follows the spec as it is edited.
              resumeKey={`${customer}/${job}${window.location.pathname}`}
              onClose={() => setHelperOpen(false)}
              getPageContext={() => (liveRef.current ? editSpecContext(liveRef.current) : '')}
              getTools={() => pageToolsRef.current ?? []}
              sessionOptions={pageAgentHost.sessionOptions}
              plugins={pageAgentHost.plugins}
              models={pageAgentHost.models}
            />
          )}
        </Drawer>
      )}
    </SideBarPage>
  )
}

export default SpecEditor
